import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getStoredAffiliateId, clearAffiliateId } from '../lib/affiliateTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuizPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  // 'quiz' | 'email' | 'submitting'
  const [step, setStep] = useState('quiz');
  const [email, setEmail] = useState('');

  // Guard against double-submission
  const isSubmittingRef = useRef(false);

  const [locationData, setLocationData] = useState({
    country_name:         null,
    country_code:         null,
    currency:             null,
    ip_address:           null,
    city:                 null,
    postal_code:          null,
    country_calling_code: null,
    region:               null,
    region_code:          null,
  });

  useEffect(() => {
    fetchQuizData();
    fetchLocationData();
  }, [quizId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentQuestionIndex]);

  useEffect(() => {
    const handlePopState = (e) => {
      e.preventDefault();
      if (step === 'email') {
        setStep('quiz');
        window.history.pushState(null, '', window.location.href);
        return;
      }
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentQuestionIndex, step]);

  const fetchQuizData = async () => {
    try {
      const [quizRes, questionsRes, answersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/quizzes/${quizId}`),
        axios.get(`${BACKEND_URL}/api/quizzes/${quizId}/questions`),
        axios.get(`${BACKEND_URL}/api/quizzes/${quizId}/answers`),
      ]);
      setQuiz(quizRes.data);
      setQuestions(questionsRes.data);
      setAnswers(answersRes.data);
    } catch (error) {
      console.error('Error fetching quiz data:', error);
      toast.error('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationData = async () => {
    try {
      const ipRes = await axios.get('https://ipapi.co/json/');
      setLocationData({
        country_name:         ipRes.data.country_name         || null,
        country_code:         ipRes.data.country_code         || null,
        currency:             ipRes.data.currency             || null,
        ip_address:           ipRes.data.ip                   || null,
        city:                 ipRes.data.city                 || null,
        postal_code:          ipRes.data.postal               || null,
        country_calling_code: ipRes.data.country_calling_code || null,
        region:               ipRes.data.region               || null,
        region_code:          ipRes.data.region_code          || null,
      });
    } catch (error) {
      console.error('Error detecting location:', error);
    }
  };

  // Called after email step — userEmail may be empty string if skipped
  const submitQuiz = async (userEmail) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setStep('submitting');

    const resultCounts = {};
    Object.values(selectedAnswers).forEach(({ resultId }) => {
      resultCounts[resultId] = (resultCounts[resultId] || 0) + 1;
    });
    const finalResultId = parseInt(
      Object.keys(resultCounts).reduce((a, b) =>
        resultCounts[a] > resultCounts[b] ? a : b
      )
    );

    const affiliateId = getStoredAffiliateId();

    try {
      const response = await axios.post(`${BACKEND_URL}/api/user-results`, {
        quiz_id:      quizId,
        result_id:    finalResultId,
        user_email:   userEmail || 'sale@quizmind.net',
        affiliate_id: affiliateId,
        ...locationData,
      });

      clearAffiliateId();
      navigate(`/results/${response.data.uid}`);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
      isSubmittingRef.current = false;
      setStep('email');
    }
  };

  const handleAnswerSelect = (answerId, resultId) => {
    const newSelectedAnswers = {
      ...selectedAnswers,
      [currentQuestion.question_id]: { answerId, resultId },
    };
    setSelectedAnswers(newSelectedAnswers);

    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }, 200);
    } else {
      // Last question — show email step after brief visual feedback
      setTimeout(() => {
        setStep('email');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
    }
  };

  const handleNext = () => {
    if (!selectedAnswers[currentQuestion.question_id]) {
      toast.warning(t('quiz.selectAnswer'));
      return;
    }
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setStep('email');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleEmailSubmit = () => {
    const trimmed = email.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t('quiz.invalidEmail', 'Please enter a valid email address'));
      return;
    }
    submitQuiz(trimmed);
  };

  const handleSkipEmail = () => {
    submitQuiz('');
  };

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswers = answers.filter(
    (a) => a.question_id === currentQuestion?.question_id
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  // ── Email / Submitting step ────────────────────────────────────────────────
  if (step === 'email' || step === 'submitting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100 flex items-center justify-center px-4">
        <AnimatePresence mode="wait">
          {step === 'submitting' ? (
            <motion.div
              key="submitting"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600 font-medium">
                {t('results.redirecting', 'Preparing your results…')}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="email"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
              className="w-full max-w-md"
            >
              <div className="bg-white/85 backdrop-blur-md rounded-3xl p-8 border border-white/50 shadow-2xl">
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-5 shadow-lg">
                  <Mail className="w-7 h-7 text-white" />
                </div>

                <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
                  {t('quiz.email', 'Email Address')}
                </h2>
                <p className="text-sm text-slate-500 text-center mb-6 leading-relaxed">
                  {t('quiz.sendEmail', "We'll send your results to this email address")}
                </p>

                {/* Input */}
                <div className="relative mb-3">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                    placeholder="you@example.com"
                    autoFocus
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-slate-900 text-sm placeholder:text-slate-400"
                  />
                </div>

                {/* Privacy note */}
                <p className="text-xs text-slate-400 text-center mb-5 leading-relaxed">
                  {t('quiz.emailPrivacy', 'Your email will only be used to send you quiz results. We respect your privacy.')}
                </p>

                {/* Legal */}
                <p className="text-xs text-slate-400 text-center mb-6 leading-relaxed">
                  {t('quiz.legalNotice.prefix', 'By continuing you accept our')}{' '}
                  <a href="/privacy" className="underline hover:text-violet-600 transition-colors">
                    {t('quiz.legalNotice.privacyPolicy', 'Privacy Policy')}
                  </a>{' '}
                  {t('quiz.legalNotice.and', 'and')}{' '}
                  <a href="/terms" className="underline hover:text-violet-600 transition-colors">
                    {t('quiz.legalNotice.termsOfService', 'Terms of Service')}
                  </a>
                </p>

                {/* Submit */}
                <button
                  onClick={handleEmailSubmit}
                  className="w-full h-12 rounded-full font-bold text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2 mb-3"
                >
                  {t('quiz.submit', 'See My Results')}
                  <ArrowRight className="w-4 h-4" />
                </button>

                {/* Skip */}
                <button
                  onClick={handleSkipEmail}
                  className="w-full h-10 rounded-full font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all text-sm"
                >
                  {t('quiz.skipEmail', 'Skip for now')}
                </button>
              </div>

              {/* Back */}
              <button
                onClick={() => setStep('quiz')}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mx-auto mt-5"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('quiz.previous', 'Previous')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ── Quiz questions ─────────────────────────────────────────────────────────
  if (!currentQuestion) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100 overflow-x-hidden">
      <div className="w-full max-w-4xl mx-auto px-4 py-4 md:py-8">
        {/* Progress */}
        <div className="mb-4 md:mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-slate-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
            <span className="text-sm font-medium text-violet-600">
              {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-white/50 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
              initial={{ width: 0 }}
              animate={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white/80 backdrop-blur-md rounded-3xl p-4 md:p-8 border border-white/50 shadow-2xl mb-4 md:mb-6"
            data-testid="quiz-question-card"
          >
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900 mb-4 md:mb-6 tracking-tight">
              {currentQuestion.question_text}
            </h2>

            <div className="space-y-3 md:space-y-4">
              {currentAnswers.map((answer) => (
                <motion.button
                  key={answer.answer_id}
                  onClick={() => handleAnswerSelect(answer.answer_id, answer.answer_result)}
                  className={`w-full p-4 md:p-6 rounded-xl text-left font-medium transition-all duration-200 text-sm md:text-base ${
                    selectedAnswers[currentQuestion.question_id]?.answerId === answer.answer_id
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg scale-105'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100 hover:scale-102'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`quiz-answer-${answer.answer_id}`}
                >
                  {answer.answer_text}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="h-12 px-4 md:px-8 rounded-full font-bold bg-white text-violet-600 border-2 border-violet-100 hover:bg-violet-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 md:gap-2 text-sm md:text-base"
            data-testid="quiz-previous-button"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">{t('quiz.previous')}</span>
            <span className="sm:hidden">Prev</span>
          </button>

          <button
            onClick={handleNext}
            disabled={!selectedAnswers[currentQuestion.question_id]}
            className="h-12 px-4 md:px-8 rounded-full font-bold text-white shadow-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 flex items-center gap-1 md:gap-2 text-sm md:text-base"
            data-testid="quiz-next-button"
          >
            <span className="hidden sm:inline">
              {currentQuestionIndex === questions.length - 1 ? t('quiz.submit') : t('quiz.next')}
            </span>
            <span className="sm:hidden">
              {currentQuestionIndex === questions.length - 1 ? 'Submit' : 'Next'}
            </span>
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}